<template>
    <div>
        <form v-if="stripeLoaded">
            <div ref="stripeCardPaymentElementContainer"
                 class="p-4 bg-white border"
                 :class="{
                     'border-green-500': complete,
                     'border-red-500': invalid
                 }">
                 <!-- Stripe payment element will be mounted here -->
            </div>
            <AlertError class="mt-3" :alert="error"></AlertError>
            <ButtonAction
                buttonColor="my-4 bg-amber-700 text-white hover:bg-amber-600 focus-visible:outline-amber-600"
                :buttonDisabled="disabled"
                :buttonClick="pay"
            >
                <template v-slot:left>
                    <span v-if="paymentAmount">Pay {{ priceCurrency(paymentAmount) }}</span>
                    <span v-else>Save Card</span>
                </template>
            </ButtonAction>
            <div class="pb-3 pt-4" v-if="disabled">
                <LoadingSpinnerInline text="Please wait while we process your request, this may take a little while."></LoadingSpinnerInline>
            </div>
        </form>
        <p class="my-2">
            <small v-if="paymentAmount">Secure checkout provided by <a class="subtle" href="https://stripe.com/" target="_blank">Stripe</a>.</small>
            <small v-else>Secure payments provided by <a class="subtle" href="https://stripe.com/" target="_blank">Stripe</a>.</small>
        </p>
    </div>
</template>

<script setup>
import {loadStripe} from '@stripe/stripe-js';

const $config = useRuntimeConfig();

const stripeLoaded = ref();
const complete = ref(false); // Stripe card payment element filled out and valid
const disabled = ref(false); // Stripe card payment element disabled
const invalid = ref(false); // Stripe card payment element invalid
const error = ref(null); // Error returned by Stripe's createPaymentMethod or handleCardPayment methods

const stripeCardPaymentElement = ref(null); // The Stripe payment card element which will be mounted to the DOM
const stripeCardPaymentElementContainer = ref(null); // DOM Ref

const props = defineProps({
    paymentIntent: { required: false },
    paymentAmount: { required: false },
    paymentError: { required: false },
    onCreatePaymentMethod: { required: false },
    onHandleCardPayment: {required: false },
});


const stripeOptions = {
    // see https://stripe.com/docs/stripe.js#element-options for details
    style: {
        base: {
            backgroundColor: '#ffffff',
            iconColor: '#32325d',
            color: '#32325d',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4'
            }
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
        },
    }
};

const Stripe = await loadStripe($config.public.stripeKey);
stripeLoaded.value = true;

function onChangeCardDetails (event) {
    error.value = null;
    complete.value = !!event.complete;
    invalid.value = !!event.error;
}

function priceCurrency (number) {
    return useUtilityFunctions().formatCurrency(number);
}

function pay () {
    if (props.paymentIntent) return confirm();
    error.value = null;
    disabled.value = true;
    let paymentMethod = {
        type: 'card',
        card: stripeCardPaymentElement.value
    };
    Stripe.createPaymentMethod(paymentMethod)
        .then((response) => {
            if (response.error) {
                if (response.error.message !== null) {
                    error.value = response.error.message || 'Your card details are incorrect.';
                }
                disabled.value = false;
            } else {
                props.onCreatePaymentMethod(response.paymentMethod);
            }
        });
}

function confirm () {
    error.value = null;
    disabled.value = true;
    Stripe.handleCardPayment(props.paymentIntent.stripe_payment_intent_client_secret, stripeCardPaymentElement.value)
        .then((response) => {
            if (response.error) {
                error.value = response.error.message || 'Apologies but something went wrong, please try again or contact support.';
                disabled.value = false;
            } else {
                props.onHandleCardPayment(response.paymentIntent);
            }
        });
}

watch(() => props.paymentIntent, (newValue) => {
    if (newValue) confirm();
});

watch(() => props.paymentError, (newValue) => {
    if (newValue) {
        error.value = newValue;
        disabled.value = false;
    }
});

watch(disabled, value => {
    stripeCardPaymentElement.value.update({disabled: value});
});

onBeforeUnmount(() => {
    stripeCardPaymentElement.value.destroy();
});

onMounted(() => {
    const cardElement = Stripe.elements().create('card', stripeOptions);
    cardElement.on('change', onChangeCardDetails);
    cardElement.mount(stripeCardPaymentElementContainer.value);

    stripeCardPaymentElement.value = cardElement;
});

</script>
